import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Banner, Button, Card, Chip, Input } from '@/components/ds-dlp';
import { AuthLayout } from '@/app/AuthLayout';
import { accountApi } from '../api/account';
import { detectTenantSlugFromHost, getPersistedTenantSlug } from '../auth/tenantSlug';

// "Forgot password?" from the sign-in page: asks the server to mail a
// one-time reset link. The tenant works as on sign-in: locked to the
// subdomain, or typed on root-domain deploys. The answer is the same whether
// or not the account exists, so the page says so.
const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state ?? {}) as { email?: string; tenantSlug?: string };

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const subdomainSlug = useMemo(() => detectTenantSlugFromHost(hostname), [hostname]);
  const slugLocked = subdomainSlug !== null;

  const [tenantSlug, setTenantSlug] = useState(subdomainSlug ?? prefill.tenantSlug ?? getPersistedTenantSlug() ?? '');
  const [email, setEmail] = useState(prefill.email ?? '');
  const [sent, setSent] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      setSent(await accountApi.forgotPassword(email.trim(), (slugLocked ? subdomainSlug! : tenantSlug).trim().toLowerCase()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    }
    setIsLoading(false);
  };

  const backToSignIn = () => navigate('/login');

  return (
    <AuthLayout subtitle="Reset your password" tenantSlug={subdomainSlug ?? getPersistedTenantSlug()}>
      <Card>
        <Card.Body>
          {sent ? (
            <div className="flex flex-col gap-4">
              <Banner variant="success">{sent}</Banner>
              <p className="text-dense text-[var(--d2d-text-secondary)]">
                Check your inbox, and the spam folder, for an email with the link. No email? Check the address and try again,
                or ask an administrator to reset your password.
              </p>
              <Button onClick={backToSignIn} className="w-full">
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {error ? <Banner variant="danger">{error}</Banner> : null}
              <p className="text-dense text-[var(--d2d-text-secondary)]">
                Enter the email address you sign in with. We'll send you a link to choose a new password.
              </p>

              {slugLocked ? (
                <div className="flex flex-col gap-1.5">
                  <span className="text-dense font-medium text-[var(--d2d-text-primary)]">Tenant</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip variant="neutral">
                      <span className="font-mono">{subdomainSlug}</span>
                    </Chip>
                    <span className="text-micro text-[var(--d2d-text-muted)]">Detected from {hostname}</span>
                  </div>
                </div>
              ) : (
                <Input
                  id="forgot-tenant"
                  label="Tenant"
                  placeholder="acme"
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  required
                  autoComplete="organization"
                  help="Your workspace identifier, as on the sign-in page."
                  className="font-mono"
                />
              )}

              <Input
                id="forgot-email"
                label="Email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />

              <Button
                type="submit"
                loading={isLoading}
                disabled={isLoading || !email.trim() || (!slugLocked && !tenantSlug.trim())}
                className="mt-1 w-full"
              >
                Send reset link
              </Button>
              <Button variant="secondary" onClick={backToSignIn} className="w-full">
                Back to sign in
              </Button>
            </form>
          )}
        </Card.Body>
      </Card>
    </AuthLayout>
  );
};

export default ForgotPassword;
